import type { ReactNode } from "react";
import { useImmer } from "use-immer";

import { Box, Stack } from "@mui/material";

import { useIsAdmin } from "@src/api/user-auths/is-admin";
import {
  type WorkspaceBodyView,
  WorkspaceViewSwitch,
} from "@src/features/agenticCodegen/workspace/components/VersionHistory/WorkspaceViewSwitch";

import { RecentRunsPanel } from "../workspace/recent-runs/RecentRunsPanel";

import { TemplateConfigWorkspace } from "./TemplateConfigWorkspace";
import type { TemplateRuleWorkspace } from "./useTemplateRuleWorkspace";

/**
 * The template rule's Advanced body: the configuration workspace, and for
 * Intelligems staff the "Recent runs" readout of recorded executions. The
 * segment and its body are both behind the admin gate, so a member never sees
 * the control and never fires the staff-only query.
 *
 * There is no version history on the rule page: edits autosave to a draft,
 * and Discard is the way back to the live version.
 */
export const TemplateRuleAdvancedTab = ({
  ruleId,
  editor,
  descriptionEditor,
}: {
  ruleId: string;
  editor: TemplateRuleWorkspace;
  descriptionEditor?: ReactNode;
}) => {
  const [bodyView, setBodyView] = useImmer<WorkspaceBodyView>("primary");
  const isAdmin = useIsAdmin() === true;

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      <WorkspaceViewSwitch
        primaryLabel="Configuration"
        horizontalPadding={1}
        topPadding={2}
        bottomPadding={1}
        showRuns={isAdmin}
        showHistory={false}
        value={bodyView}
        onChange={(next) => setBodyView(() => next)}
      />
      <Box
        sx={{
          display: bodyView === "primary" ? "flex" : "none",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          p: 1,
          overflow: "hidden",
        }}
      >
        <TemplateConfigWorkspace
          editor={editor}
          descriptionEditor={descriptionEditor}
          // This tab only renders behind the Advanced toggle, so its body is
          // always the expert view by construction.
          advancedEnabled
        />
      </Box>
      {bodyView === "runs" && isAdmin ? (
        <Stack sx={{ flex: 1, minHeight: 0, p: 1 }}>
          <RecentRunsPanel ruleId={ruleId} />
        </Stack>
      ) : null}
    </Stack>
  );
};
